import type { FirebaseError } from 'firebase/app'

const FIREBASE_MESSAGE_BY_CODE = {
  'auth/user-not-found': 'No account found with this email address',
  'auth/wrong-password': 'Incorrect password',
  'auth/invalid-email': 'Please enter a valid email address',
  'auth/unverified-email': 'Please verify your email address to continue',
  'auth/user-disabled': 'This account has been disabled',
  'auth/missing-code': 'Please enter the verification code',
  'auth/invalid-credential': 'The provided credentials are invalid',
  'auth/network-request-failed':
    'Unable to connect to the server. Please check your internet connection',
  'auth/too-many-requests': 'Too many attempts. Please try again later',
  'auth/email-already-in-use': 'An account already exists with this email',
  'auth/weak-password': 'Password should be at least 8 characters',
  'auth/operation-not-allowed':
    'This operation is not allowed. Please contact support',
  'auth/invalid-phone-number': 'The phone number is invalid',
  'auth/missing-phone-number': 'Please provide a phone number',
  'auth/quota-exceeded': 'SMS quota exceeded. Please try again later',
  'auth/code-expired': 'The verification code has expired',
  'auth/captcha-check-failed':
    'reCAPTCHA verification failed. Please try again',
  'auth/missing-verification-id':
    'Please complete the reCAPTCHA verification first',
  'auth/missing-email': 'Please provide an email address',
  'auth/invalid-action-code': 'The email link is invalid or has expired',
  'auth/credential-already-in-use':
    'An account already exists with this email. Please sign in with that account',
  'auth/requires-recent-login':
    'This operation requires a recent login. Please sign in again',
  'auth/provider-already-linked':
    'This phone number is already linked to another account',
  'auth/invalid-verification-code':
    'Invalid verification code. Please try again',
  'auth/account-exists-with-different-credential':
    'An account already exists with this email. Please sign in with the original provider',
  'auth/display-name-required': 'Please provide a display name',
  'auth/second-factor-already-in-use':
    'This phone number is already enrolled with this account',
} as const

// Firestore surfaces connectivity problems through a small set of gRPC status
// codes. We collapse them into one user-facing connection message; everything
// else (including schema-parse failures) falls back to a generic message so
// raw internals are never shown to the user.
const FIRESTORE_NETWORK_CODES = new Set([
  'unavailable',
  'deadline-exceeded',
  'cancelled',
  'aborted',
])

const FIRESTORE_CONNECTION_MESSAGE =
  'Unable to connect to the server. Please check your internet connection and try again'

const FIRESTORE_GENERIC_MESSAGE = 'Something went wrong. Please try again'

/**
 * Whether a failure was the connection rather than the request.
 *
 * Exported so a caller can ACT on the difference and not only word it: telling
 * a user to check their internet when Firestore actually said
 * `permission-denied` sends them chasing their wifi over a rules bug.
 */
export const isFirestoreConnectionError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'code' in error &&
  FIRESTORE_NETWORK_CODES.has((error as FirebaseError).code)

export const getFirestoreErrorMessage = (error: unknown): string =>
  isFirestoreConnectionError(error)
    ? FIRESTORE_CONNECTION_MESSAGE
    : FIRESTORE_GENERIC_MESSAGE

export interface AuthError {
  code: string
  message: string
  cause?: unknown
}

export const normalizeAuthError = (error: unknown): AuthError => {
  if (typeof error === 'object' && error && 'code' in error) {
    const code = (error as FirebaseError).code
    const message =
      code in FIREBASE_MESSAGE_BY_CODE
        ? FIREBASE_MESSAGE_BY_CODE[
            code as keyof typeof FIREBASE_MESSAGE_BY_CODE
          ]
        : `An unexpected authentication error occurred: ${code}`

    return {
      code,
      message,
      cause: error,
    }
  }

  return {
    code: 'unknown',
    message: `An unexpected authentication error occurred: ${error}`,
    cause: error,
  }
}
